import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, TextField, Button, MenuItem, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert, Tooltip, Pagination, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Restaurant as ServiceIcon, CheckCircle as PaidIcon,
  Schedule as CreditIcon, Groups as PeopleIcon, Layers as ValueIcon,
  Print as PrintIcon, GridOn as ExcelIcon, Close as CloseIcon,
  Info as InfoIcon, BarChart as SummaryIcon, Check as TickIcon,
  CalendarMonth as DateIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ═══════════════════════════════════════════════════════════════════
//  The list the kitchen works from.
//
//  Everything else in this module is written for whoever handles the
//  money. This one is for the people who hand out the thing being paid
//  for — the cook, the bus conductor, whoever is at the counter. They
//  need one question answered, today, for one class: WHO DO I SERVE?
//
//  So it is two lists side by side rather than a ledger. Green: paid,
//  hand it over. Orange: taking it on credit, which is a decision
//  somebody has already made, so hand it over and let the office chase
//  it. Anyone marked as not taking it is on neither list — they are not
//  being served, and putting them in front of a cook is noise.
//
//  Read-only, on purpose. Marking and money belong on the teacher's
//  sheet and the approvals screen, where the gates live. A second place
//  to change a figure is a second place for the two to disagree.
// ═══════════════════════════════════════════════════════════════════

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const money = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const prettyType = (t) => String(t || '')
  .replace(/_+$/, '').replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const isoToday = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const longDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d || '')
  ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined,
      { day: 'numeric', month: 'long', year: 'numeric' })
  : (d || '');

const PAGE = 10;

// A day settled out of money already on account belongs on the GREEN
// list: the school has the money, the learner just did not hand cash
// over this morning. Putting them on the orange list would have the cook
// querying a debt that does not exist.
const settledFromAdvance = (r) => !!r && r.status !== 'absent'
  && Number(r.credit_used || 0) > 0
  && (Number(r.daily_amount || 0) - Number(r.amount_paid || 0)
      - Number(r.credit_used || 0)) <= 0.009;

// ── One headline figure ────────────────────────────────────────────
function StatCard({ icon, tone, label, value, sub }) {
  return (
    <Card elevation={0} sx={{
      px: 1.8, py: 1.5, borderRadius: 3, flex: '1 1 178px', minWidth: 166,
      bgcolor: tone.bg, border: `1px solid ${tone.line}`,
    }}>
      <Stack direction="row" spacing={1.4} alignItems="center">
        <Box sx={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          bgcolor: tone.chip, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 11.5, fontWeight: 700, color: tone.fg, lineHeight: 1.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 23, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, ...TNUM }}>
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: tone.sub, ...TNUM }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

// ── One of the two lists ───────────────────────────────────────────
function ServeList({ tone, title, note, rows, showRemarks, unit }) {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  useEffect(() => { if (page > pages) setPage(1); }, [page, pages]);
  const shown = rows.slice((page - 1) * PAGE, page * PAGE);

  return (
    <Card elevation={0} sx={{
      flex: '1 1 440px', minWidth: 0, borderRadius: 3, overflow: 'hidden',
      border: '1px solid #e3eaf3', bgcolor: '#fff',
      display: 'flex', flexDirection: 'column',
    }}>
      <Stack direction="row" spacing={1.2} alignItems="center"
        sx={{ px: 1.8, py: 1.3, bgcolor: tone.head, color: '#fff' }}>
        {tone.icon}
        <Typography sx={{ fontSize: 14, fontWeight: 800, flex: 1 }}>
          {title}
        </Typography>
        <Chip size="small" label={`${rows.length} Students`}
          sx={{
            height: 24, fontSize: 11, fontWeight: 800,
            bgcolor: 'rgba(255,255,255,.22)', color: '#fff',
          }} />
      </Stack>

      <TableContainer sx={{ flex: 1 }}>
        <Table size="small" sx={{
          '& th': {
            bgcolor: tone.sub, color: '#334155', fontWeight: 800,
            fontSize: 10.5, py: 0.9, borderBottom: `1px solid ${tone.line}`,
          },
          '& td': {
            fontSize: 12, py: 0.55, color: '#1e293b',
            borderBottom: '1px solid #f1f5f9',
          },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 34 }}>#</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Student Name</TableCell>
              <TableCell sx={{ width: 72 }}>Class</TableCell>
              <TableCell align="center" sx={{ width: 86 }}>{unit}</TableCell>
              {showRemarks && <TableCell sx={{ minWidth: 92 }}>Remarks</TableCell>}
              {!showRemarks && <TableCell sx={{ width: 34 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showRemarks ? 5 : 5} align="center"
                  sx={{ py: 5, color: '#94a3b8', fontSize: 12.5 }}>
                  Nobody on this list today.
                </TableCell>
              </TableRow>
            )}
            {shown.map((r, i) => (
              <TableRow key={`${r.student_id}|${r.income_type}`} hover>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 600, ...TNUM }}>
                  {(page - 1) * PAGE + i + 1}
                </TableCell>
                <TableCell>
                  <Tooltip title={r.student_id || ''}>
                    <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                      {r.student_name}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>{r.class_name}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={r.word}
                    sx={{
                      height: 21, minWidth: 68, borderRadius: 999,
                      fontSize: 9.5, fontWeight: 800,
                      bgcolor: tone.pillBg, color: tone.pillFg, border: 'none',
                    }} />
                </TableCell>
                {showRemarks ? (
                  <TableCell sx={{ fontSize: 10.5, color: '#64748b' }}>
                    {r.notes || '-'}
                  </TableCell>
                ) : (
                  <TableCell align="center">
                    <TickIcon sx={{ fontSize: 16, color: '#22a559' }} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 1.6, py: 1, borderTop: '1px solid #eef2f7' }}>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          {rows.length === 0
            ? note
            : `Showing ${(page - 1) * PAGE + 1} – ${Math.min(page * PAGE, rows.length)}`
              + ` of ${rows.length}`}
        </Typography>
        {pages > 1 && (
          <Pagination size="small" shape="rounded" count={pages} page={page}
            onChange={(_, p) => setPage(p)} />
        )}
      </Stack>
    </Card>
  );
}

export default function ServiceListTab({ classes = [], incomeTypes = [] }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));

  const [date, setDate] = useState(isoToday);
  const [type, setType] = useState('');
  const [cls,  setCls]  = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);

  // The whole day comes down in one request, unfiltered, and the
  // collection is chosen from it in the browser.
  //
  // Asking the server per collection meant the dropdown had to be built
  // from the school's FULL list of income types — so a cook picking
  // through "Feeding, Transportation, School Fees, PTA Levy, Exams
  // Fee…" to find the two that actually ran on the 21st. The day itself
  // knows which ran; ask it once and the list answers for itself.
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await API.get('/daily-subscriptions/day-analysis', { params: { date } });
      setData(r.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load the day');
      setData(null);
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Only the collections that HAPPENED on this date. A session that was
  // opened counts, even if nobody has been marked on it yet — that is a
  // round still being walked, not a collection that did not run.
  const ranTypes = useMemo(() => {
    const s = new Set();
    for (const r of data?.sessions || []) if (r.income_type) s.add(r.income_type);
    for (const r of data?.roster   || []) if (r.income_type) s.add(r.income_type);
    return [...s].sort();
  }, [data]);

  // Land on one, and never sit on a collection that did not run today.
  useEffect(() => {
    if (!ranTypes.length) return;
    if (!type || !ranTypes.includes(type)) setType(ranTypes[0]);
  }, [ranTypes, type]);

  // ── Split the day into the two lists ────────────────────────────
  //
  // Done twice: once narrowed to the class on screen, for the lists,
  // and once across every class for the summary. The summary is asked
  // exactly so somebody can see the classes they are NOT looking at.
  const classify = useCallback((all) => {
    const p = [], c = [], n = [];
    const per = new Map();
    let seenRate = 0;

    for (const r of all) {
      const fee  = Number(r.daily_amount || 0);
      if (fee > 0 && !seenRate) seenRate = fee;
      const key = String(r.class_name || '—');
      if (!per.has(key)) per.set(key, { cls: key, paid: 0, credit: 0, notServed: 0, value: 0 });
      const row = per.get(key);

      if (r.status === 'absent') {
        n.push({ ...r, word: 'NOT TAKEN' });
        row.notServed += 1;
        continue;
      }
      // Paid in cash, paid in part, or covered by advance — all of them
      // are served, and all of them are the school's money.
      const settled = settledFromAdvance(r);
      const got = Number(r.amount_paid || 0) + Number(r.credit_used || 0);
      if (settled || got >= fee - 0.009) {
        p.push({ ...r, word: settled ? 'ADVANCE' : 'PAID' });
        row.paid += 1; row.value += fee;
      } else {
        c.push({ ...r, word: got > 0.009 ? 'PART' : 'CREDIT' });
        row.credit += 1; row.value += fee;
      }
    }

    const sortByName = (a, b) =>
      String(a.class_name).localeCompare(String(b.class_name))
      || String(a.student_name).localeCompare(String(b.student_name));

    return {
      paid: p.sort(sortByName),
      credit: c.sort(sortByName),
      notServed: n.sort(sortByName),
      byClass: [...per.values()].sort((a, b) => a.cls.localeCompare(b.cls)),
      rate: seenRate,
    };
  }, []);

  // For the chosen collection, whatever class is on screen.
  const ofType = useMemo(
    () => (data?.roster || []).filter(r => !type || r.income_type === type),
    [data, type]);

  const onScreen = useMemo(
    () => classify(cls ? ofType.filter(r => String(r.class_name || '') === cls) : ofType),
    [classify, ofType, cls]);
  const { paid, credit, notServed, rate } = onScreen;

  // Every class, for the summary popup — the class filter deliberately
  // does NOT apply to it.
  const byClass = useMemo(() => classify(ofType).byClass, [classify, ofType]);

  const totalServed = paid.length + credit.length;
  const totalValue  = onScreen.byClass.reduce((a, r) => a + r.value, 0);
  const listTitle = type ? `${prettyType(type)} List` : 'Service List';

  // ── Print ───────────────────────────────────────────────────────
  const printList = () => {
    const block = (heading, rows, withRemarks) => `
      <h2>${heading} — ${rows.length}</h2>
      <table>
        <thead><tr><th>#</th><th>Student Name</th><th>Class</th><th>Status</th>
          ${withRemarks ? '<th>Remarks</th>' : ''}</tr></thead>
        <tbody>${rows.map((r, i) => `<tr>
          <td>${i + 1}</td><td class="nm">${r.student_name}</td>
          <td>${r.class_name || ''}</td><td>${r.word}</td>
          ${withRemarks ? `<td class="nm">${r.notes || '-'}</td>` : ''}
        </tr>`).join('') || `<tr><td colspan="${withRemarks ? 5 : 4}">Nobody</td></tr>`}</tbody>
      </table>`;

    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>${listTitle} — ${longDate(date)}</title>
      <style>
        @page { size: A4 portrait; margin: 12mm; }
        body { font-family: Arial, Helvetica, sans-serif; color:#111; }
        h1 { font-size: 16pt; margin:0 0 2px; }
        h2 { font-size: 11pt; margin: 14px 0 4px; }
        .sub { font-size: 9.5pt; color:#555; margin-bottom: 6px; }
        .fig { font-size: 10pt; margin: 6px 0 2px; }
        .fig b { display:inline-block; min-width: 150px; }
        table { width:100%; border-collapse: collapse; }
        th, td { border:1px solid #999; padding:3px 5px; font-size:9pt; text-align:center; }
        th { background:#eef2f7; }
        .nm { text-align:left; }
        .sum td, .sum th { font-size: 9pt; }
      </style></head><body>
      <h1>${listTitle}${cls ? ` — ${cls}` : ''}</h1>
      <div class="sub">${longDate(date)} &middot; printed ${new Date().toLocaleString()}</div>
      <div class="fig"><b>To serve</b> ${totalServed}
        &nbsp; <b>Paid</b> ${paid.length} &nbsp; <b>On credit</b> ${credit.length}
        &nbsp; <b>Not taking it</b> ${notServed.length}</div>
      <div class="fig"><b>Value</b> GH¢ ${money(totalValue)}${rate ? ` (GH¢ ${money(rate)} each)` : ''}</div>
      ${block('PAID — serve these', paid, false)}
      ${block('CREDIT — serve these, office to follow up', credit, true)}
      <h2>Summary by class</h2>
      <table class="sum">
        <thead><tr><th>Class</th><th>Paid</th><th>Credit</th><th>To serve</th>
          <th>Not taking</th><th>Value (GH¢)</th></tr></thead>
        <tbody>${byClass.map(r => `<tr>
          <td>${r.cls}</td><td>${r.paid}</td><td>${r.credit}</td>
          <td><b>${r.paid + r.credit}</b></td><td>${r.notServed}</td>
          <td>${money(r.value)}</td></tr>`).join('')}</tbody>
        <tfoot><tr><td><b>Total</b></td><td><b>${paid.length}</b></td>
          <td><b>${credit.length}</b></td><td><b>${totalServed}</b></td>
          <td><b>${notServed.length}</b></td><td><b>${money(totalValue)}</b></td></tr></tfoot>
      </table></body></html>`;

    const w = window.open('', '_blank');
    if (!w) { swal.warning('Allow pop-ups to print this list.'); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 300);
  };

  // ── Excel ───────────────────────────────────────────────────────
  const exportExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      wb.created = new Date();

      const sheet = (name, rows, withRemarks) => {
        const ws = wb.addWorksheet(name.slice(0, 28));
        ws.addRow([`${listTitle} — ${name}`]).font = { bold: true, size: 13 };
        ws.addRow([longDate(date) + (cls ? ` · ${cls}` : '')]).font = { italic: true, size: 10 };
        ws.addRow([]);
        const hr = ws.addRow(['#', 'Student Name', 'Index', 'Class', 'Status',
          ...(withRemarks ? ['Remarks'] : [])]);
        hr.font = { bold: true };
        hr.eachCell(c => {
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2F7' } };
          c.border = { top: { style: 'thin' }, left: { style: 'thin' },
                       bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        rows.forEach((r, i) => ws.addRow([
          i + 1, r.student_name, r.student_id, r.class_name || '', r.word,
          ...(withRemarks ? [r.notes || ''] : []),
        ]));
        ws.columns.forEach((c, i) => { c.width = i === 1 ? 28 : 14; });
        ws.views = [{ state: 'frozen', ySplit: 4 }];
      };

      sheet('Paid', paid, false);
      sheet('Credit', credit, true);

      const ws = wb.addWorksheet('Summary');
      ws.addRow(['Class', 'Paid', 'Credit', 'To serve', 'Not taking', 'Value (GH¢)'])
        .font = { bold: true };
      for (const r of byClass) {
        ws.addRow([r.cls, r.paid, r.credit, r.paid + r.credit, r.notServed,
                   Number(r.value.toFixed(2))]);
      }
      ws.addRow(['Total', paid.length, credit.length, totalServed, notServed.length,
                 Number(totalValue.toFixed(2))]).font = { bold: true };
      ws.columns.forEach(c => { c.width = 15; });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${listTitle}-${cls || 'all'}-${date}`
        .replace(/[^A-Za-z0-9_-]+/g, '_') + '.xlsx';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      swal.error(e?.message || 'Excel export failed');
    }
  };

  return (
    <Box>
      {/* ══ Header: what list, which day, which class ═════════════ */}
      <Card elevation={0} sx={{
        p: { xs: 1.4, md: 1.9 }, mb: 1.4, borderRadius: 3,
        bgcolor: '#fff', border: '1px solid #e6ecf4',
      }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.6}
          alignItems={{ lg: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.6} alignItems="center">
            <Box sx={{
              width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
              bgcolor: '#2f6fb0', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ServiceIcon sx={{ fontSize: 25 }} />
            </Box>
            <Box>
              <Typography sx={{
                fontSize: { xs: 17, md: 21 }, fontWeight: 800,
                color: '#1e3a6b', lineHeight: 1.2,
              }}>
                {listTitle} for Service
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                Who to serve today. Paid students are in the green list,
                students on credit are in the orange list.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" type="date" label="Date" value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 162 }}
              InputProps={{ startAdornment: (
                <InputAdornment position="start">
                  <DateIcon sx={{ fontSize: 17, color: '#94a3b8' }} />
                </InputAdornment>
              ) }} />
            <TextField select size="small" label="Collection" value={type}
              onChange={(e) => setType(e.target.value)} sx={{ minWidth: 152 }}
              helperText={ranTypes.length ? undefined : 'Nothing ran on this date'}
              FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.4 } }}>
              {ranTypes.length === 0 && <MenuItem value="">—</MenuItem>}
              {ranTypes.map(t => <MenuItem key={t} value={t}>{prettyType(t)}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Class" value={cls}
              onChange={(e) => setCls(e.target.value)} sx={{ minWidth: 140 }}>
              <MenuItem value="">All classes</MenuItem>
              {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <Tooltip title="Reload">
              <span>
                <Button size="small" onClick={load} sx={{ minWidth: 0, px: 1 }}>
                  <RefreshIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 1.4 }}>{error}</Alert>}

      {/* ══ The figures the kitchen counts by ═════════════════════ */}
      <Stack direction="row" spacing={1.2} sx={{ mb: 1.4 }} flexWrap="wrap" useFlexGap>
        <StatCard icon={<PaidIcon />} label="Paid Students"
          value={paid.length} sub={rate ? `(GH¢ ${money(rate)} each)` : null}
          tone={{ bg: '#f1faf5', line: '#cfe7d6', fg: '#15803d', chip: '#22a559', sub: '#4a9b6b' }} />
        <StatCard icon={<CreditIcon />} label="Credit Students"
          value={credit.length} sub={rate ? `(GH¢ ${money(rate)} each)` : null}
          tone={{ bg: '#fdf8ed', line: '#f3e0ba', fg: '#b8860b', chip: '#efa22e', sub: '#b8913f' }} />
        <StatCard icon={<PeopleIcon />} label="Total to Serve"
          value={totalServed}
          sub={notServed.length ? `${notServed.length} not taking it` : null}
          tone={{ bg: '#f2f7fd', line: '#dce7f5', fg: '#1c5b96', chip: '#4a90d9', sub: '#6d8dae' }} />
        <StatCard icon={<ValueIcon />} label="Total Value"
          value={`GH¢ ${money(totalValue)}`}
          tone={{ bg: '#f7f3fd', line: '#e2d8f5', fg: '#6b3fa0', chip: '#8b5cd6', sub: '#8a76a8' }} />

        {/* The class breakdown, one tap away. It ignores the Class
            filter on purpose — it is asked precisely to see the classes
            you are not currently looking at. */}
        <Card elevation={0} sx={{
          px: 1.8, py: 1.5, borderRadius: 3, flex: '1 1 190px', minWidth: 178,
          bgcolor: '#2f6fb0', color: '#fff', cursor: 'pointer',
          transition: 'background-color .12s',
          '&:hover': { bgcolor: '#1e3a6b' },
        }} onClick={() => setSummaryOpen(true)}>
          <Stack direction="row" spacing={1.4} alignItems="center">
            <Box sx={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              bgcolor: 'rgba(255,255,255,.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SummaryIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.3 }}>
                Class Summary
              </Typography>
              <Typography sx={{ fontSize: 10.5, opacity: .85 }}>
                {byClass.length} class{byClass.length === 1 ? '' : 'es'} · tap to open
              </Typography>
            </Box>
          </Stack>
        </Card>
      </Stack>

      {/* ══ The two lists ════════════════════════════════════════ */}
      {loading ? (
        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.4} alignItems="stretch">
          <ServeList
            rows={paid} unit="Status" showRemarks={false}
            title="PAID — Students Who Have Paid"
            note="Nobody has paid yet today."
            tone={{
              head: '#1f8a4c', sub: '#f1faf5', line: '#cfe7d6',
              pillBg: '#dff3e7', pillFg: '#15803d',
              icon: <PaidIcon sx={{ fontSize: 21 }} />,
            }} />
          <ServeList
            rows={credit} unit="Status" showRemarks
            title="CREDIT — Students on Credit"
            note="Nobody is on credit today."
            tone={{
              head: '#e08a1e', sub: '#fdf8ed', line: '#f3e0ba',
              pillBg: '#fdf0d8', pillFg: '#a1610a',
              icon: <CreditIcon sx={{ fontSize: 21 }} />,
            }} />
        </Stack>
      )}

      {/* ══ Note + the two actions ═══════════════════════════════ */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} sx={{ mt: 1.4 }}
        alignItems="stretch">
        <Card elevation={0} sx={{
          p: 1.7, borderRadius: 3, flex: 1,
          bgcolor: '#f2f7fd', border: '1px solid #dce7f5',
        }}>
          <Stack direction="row" spacing={1.2}>
            <InfoIcon sx={{ fontSize: 19, color: '#2f6fb0', mt: 0.1 }} />
            <Box>
              <Typography sx={{ fontSize: 11.5, color: '#475569', lineHeight: 1.7 }}>
                • Both lists are served. <b>Credit</b> means the office will follow
                it up — it is not a reason to turn a child away.<br />
                • Anyone marked as not taking it is on neither list.<br />
                • This list is read-only. Marking and money are changed on the
                teacher's sheet, so the two can never disagree.
              </Typography>
            </Box>
          </Stack>
        </Card>

        <Stack direction="row" spacing={1.2} alignItems="center"
          sx={{ flexShrink: 0, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={printList}
            disabled={totalServed === 0}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              px: 2.6, py: 1.2, color: '#334155', borderColor: '#d8e1ec',
            }}>
            Print List
          </Button>
          <Button variant="contained" disableElevation startIcon={<ExcelIcon />}
            onClick={exportExcel} disabled={totalServed === 0}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2,
              px: 2.6, py: 1.2, bgcolor: '#1f8a4c', '&:hover': { bgcolor: '#15803d' },
            }}>
            Export
          </Button>
        </Stack>
      </Stack>

      {isPhone && (
        <Alert severity="info" sx={{ mt: 1.4, fontSize: 12 }}>
          The two lists stack on a phone. <b>Print List</b> gives both on one page.
        </Alert>
      )}

      <ClassSummaryDialog
        open={summaryOpen} onClose={() => setSummaryOpen(false)}
        rows={byClass} title={listTitle} date={date} rate={rate} />
    </Box>
  );
}

// ═══ The class breakdown ═══════════════════════════════════════════
//
// One glance, then close. Class, how many paid, how many on credit,
// how many to serve, and what it is worth — for every class the chosen
// collection ran in, whatever class is selected behind it.
function ClassSummaryDialog({ open, onClose, rows, title, date, rate }) {
  const t = rows.reduce((a, r) => ({
    paid: a.paid + r.paid,
    credit: a.credit + r.credit,
    notServed: a.notServed + r.notServed,
    value: a.value + r.value,
  }), { paid: 0, credit: 0, notServed: 0, value: 0 });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ p: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="center"
          sx={{ px: 2.2, py: 1.6, bgcolor: '#f2f7fd', borderBottom: '1px solid #dce7f5' }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            bgcolor: '#2f6fb0', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <SummaryIcon sx={{ fontSize: 21 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#1e3a6b', lineHeight: 1.25 }}>
              Class Summary
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
              {title} · {longDate(date)}
              {rate ? ` · GH¢ ${money(rate)} each` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <TableContainer>
          <Table size="small" stickyHeader sx={{
            '& th': {
              bgcolor: '#f3f7fc', color: '#334a6b', fontWeight: 800,
              fontSize: 10.5, py: 1,
            },
            '& td': { fontSize: 12.5, py: 0.7, borderBottom: '1px solid #eef2f7' },
          }}>
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell align="center">Paid</TableCell>
                <TableCell align="center">Credit</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="right">Value (GH¢)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5, color: '#94a3b8' }}>
                    Nothing marked for this collection on this date.
                  </TableCell>
                </TableRow>
              )}
              {rows.map(r => (
                <TableRow key={r.cls} hover>
                  <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{r.cls}</TableCell>
                  <TableCell align="center" sx={{ ...TNUM, color: '#15803d', fontWeight: 700 }}>
                    {r.paid}
                  </TableCell>
                  <TableCell align="center" sx={{ ...TNUM, color: '#a1610a', fontWeight: 700 }}>
                    {r.credit}
                  </TableCell>
                  <TableCell align="center" sx={{ ...TNUM, fontWeight: 800 }}>
                    {r.paid + r.credit}
                  </TableCell>
                  <TableCell align="right" sx={{ ...TNUM, fontWeight: 700 }}>
                    {money(r.value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {rows.length > 0 && (
              <TableBody>
                <TableRow sx={{ bgcolor: '#f7fafd' }}>
                  <TableCell sx={{ fontWeight: 800, borderTop: '2px solid #dce7f5' }}>
                    Total
                    {t.notServed > 0 && (
                      <Box component="span" sx={{
                        fontWeight: 600, fontSize: 10, color: '#94a3b8', ml: 0.7,
                      }}>
                        ({t.notServed} not taking it)
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ ...TNUM, fontWeight: 800, color: '#15803d', borderTop: '2px solid #dce7f5' }}>
                    {t.paid}
                  </TableCell>
                  <TableCell align="center" sx={{ ...TNUM, fontWeight: 800, color: '#a1610a', borderTop: '2px solid #dce7f5' }}>
                    {t.credit}
                  </TableCell>
                  <TableCell align="center" sx={{ ...TNUM, fontWeight: 800, borderTop: '2px solid #dce7f5' }}>
                    {t.paid + t.credit}
                  </TableCell>
                  <TableCell align="right" sx={{ ...TNUM, fontWeight: 800, borderTop: '2px solid #dce7f5' }}>
                    {money(t.value)}
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.2, borderTop: '1px solid #e8eef6' }}>
        <Button variant="contained" disableElevation onClick={onClose}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
            bgcolor: '#2f6fb0', '&:hover': { bgcolor: '#1e3a6b' },
          }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
